import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiMapPin, FiClock, FiMessageSquare } = FiIcons;

const Contact = () => {
  const contactMethods = [
    { icon: FiPhone, title: 'Phone', info: '(818) 471-4255', link: 'tel:8184714255', description: 'Speak directly with our licensed insurance professionals' },
    { icon: FiMail, title: 'Email', info: 'info@burbanksinsuranceservices.com', link: 'mailto:info@burbanksinsuranceservices.com', description: 'Send us your questions and we\'ll respond promptly' },
    { icon: FiMapPin, title: 'Service Area', info: 'All of California', link: null, description: 'Licensed to serve businesses throughout California' }
  ];

  const businessHours = [
    { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Saturday', hours: '10:00 AM - 4:00 PM' },
    { day: 'Sunday', hours: 'By Appointment' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-900 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Contact Burbank Insurance Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto"
            >
              Ready to protect your California business? Our licensed insurance professionals are here to help you find the right coverage at the right price.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to connect with our team. Choose what works best for you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={method.icon} className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{method.title}</h3>
                {method.link ? (
                  <a
                    href={method.link}
                    className="text-lg font-medium text-indigo-600 hover:text-indigo-700 transition-colors block mb-4"
                  >
                    {method.info}
                  </a>
                ) : (
                  <p className="text-lg font-medium text-gray-900 mb-4">{method.info}</p>
                )}
                <p className="text-gray-600">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Hours & License Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gray-50 p-8 rounded-xl"
            >
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiClock} className="h-8 w-8 text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Business Hours</h3>
              </div>
              <div className="space-y-4">
                {businessHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-900">{schedule.day}</span>
                    <span className="text-gray-600">{schedule.hours}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Emergency Claims:</strong> Available 24/7 through our insurance partners
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-indigo-50 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Licensed & Trusted</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMessageSquare} className="h-6 w-6 text-indigo-600" />
                  <span className="text-gray-800">CA Insurance License #6013543</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMapPin} className="h-6 w-6 text-indigo-600" />
                  <span className="text-gray-800">Serving all of California</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="h-6 w-6 text-indigo-600" />
                  <span className="text-gray-800">Licensed insurance professionals</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-200">
                <h4 className="font-semibold text-gray-900 mb-2">Our Partnership</h4>
                <div className="flex items-center space-x-3">
                  <img
                    src="https://app1.sharemyimage.com/2025/05/19/burbank-insurance-services-in-california-logo.png"
                    alt="Burbank Insurance"
                    className="h-8"
                  />
                  <span className="text-lg font-bold text-indigo-600">+</span>
                  <img
                    src="https://cdn1.sharemyimage.com/smi/2025/09/11/Coterie_Logo_Trans_FullColor-1024x185.png"
                    alt="Coterie Insurance"
                    className="h-6"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Local expertise meets innovative insurance technology
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Quick answers to common questions about business insurance in California.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How quickly can I get a quote?
              </h3>
              <p className="text-gray-600">
                Through our partnership with Coterie Insurance, you can get an instant quote online in under 5 minutes. For complex businesses, our agents can provide custom quotes within 24 hours.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What types of businesses do you insure?
              </h3>
              <p className="text-gray-600">
                We provide insurance for all types of California businesses, from tech startups to construction companies, retail stores to professional services firms.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you offer payment plans?
              </h3>
              <p className="text-gray-600">
                Yes, we offer flexible payment options including monthly, quarterly, and annual payment plans to fit your business cash flow needs.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Can I modify my coverage later?
              </h3>
              <p className="text-gray-600">
                Absolutely! As your business grows and changes, we can adjust your coverage to ensure you always have the protection you need.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Don't Wait - Protect Your Business Today
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Every day without proper insurance coverage puts your California business at risk. Get protected now with fast, affordable coverage.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get My Price Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;